/**
 * RouteConfigV2.js
 *
 * Central route registry for the V2 (Ant Design) app.
 *
 * ProtectedRoutesV2 reads this array and generates <Route> elements
 * automatically via EntitlementGuard — you never need to edit
 * ProtectedRoutesV2 to add a new page, only this file.
 *
 * ─── RouteDefinition shape ────────────────────────────────────────────────
 *
 *   path                 String    — Absolute URL path, must start with "/".
 *
 *   element              ReactNode — Default component for this route.
 *                                   Used when `resolveElement` is not defined.
 *
 *   resource             String?   — Resource key matching the API.
 *                                   e.g. "jobs_library", "people_plus_ai".
 *                                   Omit to allow any authenticated user.
 *
 *   requiredEntitlements String[]  — At least one must exist in the user's
 *                                   entitlements for `resource`.
 *                                   e.g. ["view"], ["inprogress"], ["compare_data"]
 *
 *   resolveElement       Function? — Optional (role: string) => ReactElement.
 *                                   Overrides `element` with a role-specific component.
 *                                   Use for online_user vs internal-user views.
 *
 * ─── Adding a new page (3 steps) ─────────────────────────────────────────
 *
 *   1. Create your page component under src/Pages/YourPage/YourPageV2.jsx
 *   2. Import it below.
 *   3. Add an object to `routeConfigV2` with the correct resource + entitlements.
 *   → Done. ProtectedRoutesV2 picks it up automatically.
 *
 * ─── Entitlements API reference ──────────────────────────────────────────
 *
 *   GET /auth/api/role/entitlements
 *   Response shape:
 *     [{ role, role_id, resources: [{ name, entitlements: ["view", ...] }] }]
 *
 *   Resource names seen in the API:
 *     jobs_library, jobs_library-job_details, jobs_library-job_compare,
 *     jobs_library-evaluation_score, jobs_library-version,
 *     people_plus_ai, skillsbridger, skillsbridger_employee,
 *     talent_mobility, talent_mobility_employee,
 *     learning_catalyst, learning_catalyst_employee,
 *     safety_check, org_metadata, entitlement, legal
 *
 *   Common entitlement values: view, create, edit, revise, publish,
 *     unpublish, compare_data, mark_critical_role, prompt, audit,
 *     inprogress (feature flag — visible but in-progress)
 */

import React from "react";

// ── V1 Components (not yet migrated to Ant Design) ───────────────────────────
import Dashboard from "Pages/Dashboard/Dashboard";
import JobsLibraryOnline from "Pages/JobsLibrary/SbJobsLibraryOnline";
import SbJobDetailsOnline from "Pages/SbJobDetails/SbJobDetailsOnline";
import LearningCatalyst from "Pages/LearningCatalyst/LearningCatalyst";
import OrgDesignLanding from "Pages/OrgDesign/OrgDesignLanding";
import OrgDesignWorkspace from "Pages/OrgDesign/OrgDesignWorkspace";
import PaicRecommendedImport from "Pages/OrgDesign/PaicRecommendedImport";
import EmployeeSkillsBridgerV2 from "Pages/SkillsBridgerV2/EmployeeSkillsBridger/EmployeeSkillsBridgerV2";
import SbSkillsMap from "Pages/SkillsBridger/SbSkillsMap";
import SbAdminAllJobsView from "Pages/SbAdminAllJobsView/SbAdminAllJobsView";
import SbUploadJd from "Pages/SbUploadJd/SbUploadJd";

// ── Admin V2 Components (Ant Design) — Profile, Employee, Role Management ────
import ProfileManagementV2 from "Pages/AdminSetup/ProfileManagement/ProfileManagementV2";
import EmployeeManagementV2 from "Pages/AdminSetup/EmployeeManagement/EmployeeManagementV2";

// ── V2 Components (Ant Design) ────────────────────────────────────────────────
import OrgRegistryManagement from "Components/OrgContext/OrgRegistryManagement/OrgRegistryManagement";
import JobsLibraryV2 from "Pages/JobsLibraryV2/JobsLibraryV2";
import SkillsBridgerV2 from "Pages/SkillsBridgerV2/HrSkillsBridger/SkillsBridgerV2";
import SbJobDetailsV2 from "Pages/SbJobDetailsV2/SbJobDetailsV2";
import SbJobCompareV2 from "Pages/JobCompareV2/SbJobCompareV2";
import PeoplePlusAIV2 from "Pages/PeoplePlusAIV2/dashboard/PeoplePlusAIV2";
import SbPACGroupAgentsV2 from "Pages/PeoplePlusAIV2/group-agents/SbPACGroupAgentsV2";
import SbAgentLibraryV2 from "Pages/PeoplePlusAIV2/agent-library/SbAgentLibraryV2";
import SbPACJdPrdViewV2 from "Pages/PeoplePlusAIV2/pac-jd-prd-view/SbPACJdPrdViewV2";
import SbPACGroupAgentDetailsV2 from "Pages/PeoplePlusAIV2/group-agent-details/SbPACGroupAgentDetailsV2";
import SbPACPeopleJDViewV2 from "Pages/PeoplePlusAIV2/pac-people-jd-view/SbPACPeopleJDViewV2";
import SbPACRoleImpactV2 from "Pages/PeoplePlusAIV2/role-impact/SbPACRoleImpactV2";
import SbPACRoleImpactDetailsV2 from "Pages/PeoplePlusAIV2/role-impact-details/SbPACRoleImpactDetailsV2";
import OrgTechContextSetup from "Components/OrgContext/OrgTechContextSetup/OrgTechContextSetup";
import EmployeeFunctionAccess from "Components/EmployeeFunctionAccess/EmployeeFunctionAccess.jsx"
import AdminSetup from "Pages/AdminSetup/AdminSetup";
import CompensationManagement from "Pages/AdminSetup/CompensationManagement/CompensationManagement";
import OrganizationHierarchy from "Pages/AdminSetup/OrganizationHierarchy/OrganizationHierarchy";
import AdminJobCodesPage from "Pages/AdminSetup/JobCodes/AdminJobCodesPage";
import RoleReporting from "Pages/AdminSetup/RoleReporting/RoleReporting";
import JdArchive from "Pages/AdminSetup/JdArchive/JdArchive";
import OwnershipTransfer from "Pages/AdminSetup/OwnershipTransfer/OwnershipTransfer";
import AdminSetupUploadJd from "Pages/AdminSetupUploadJd/AdminSetupUploadJd";
import GroupSkillsMapV2 from "Pages/SkillsBridgerV2/HrSkillsBridger/SkillsMap/GroupSkillsMapV2";
import EmployeeSkillsMapV2 from "Pages/SkillsBridgerV2/HrSkillsBridger/SkillsMap/EmployeeSkillsMapV2";
import GroupSkillsProficiencyV2 from "Pages/SkillsBridgerV2/HrSkillsBridger/GroupSkillsProficiency/GroupSkillsProficiencyV2";
import JdTemplates from "Components/JdTemplates/JdTemplates";
import JdTemplateDetail from "Components/JdTemplates/JdTemplateDetail";
import SystemAdminSetup from "Pages/SystemAdminSetup/SystemAdminSetup";
import LegalEntityManagement from "Pages/AdminSetup/components/LegalEntityManagement/LegalEntityManagement";
import JdManagement from "Pages/AdminSetup/JdManagement/JdManagement";
import TalentMobilitySettings from "Pages/AdminSetup/TalentMobilitySettings/TalentMobilitySettings";

// ── Talent Mobility V2 (Ant Design) ──────────────────────────────────────────
import TalentMobilityManagerV2 from "Pages/TalentMobilityV2/TalentMobilityManagerV2";
import RoleDetailV2 from "Pages/TalentMobilityV2/RoleDetailV2";
import SuccessionPlanningV2 from "Pages/TalentMobilityV2/SuccessionPlanningV2";
import RedeploymentCenterV2 from "Pages/TalentMobilityV2/RedeploymentCenterV2";
import RedeploymentReductionDetailV2 from "Pages/TalentMobilityV2/RedeploymentReductionDetailV2";
import WorkforcePlanningV2 from "Pages/TalentMobilityV2/WorkforcePlanningV2";
import PaicOrgRedesignV2 from "Pages/TalentMobilityV2/PaicOrgRedesignV2";
import PaicReductionDetailV2 from "Pages/TalentMobilityV2/PaicReductionDetailV2";
import ViewPlanningV2 from "Pages/TalentMobilityV2/ViewPlanningV2";
import TalentMobilityEmployeeV2 from "Pages/TalentMobilityV2/TalentMobilityEmployeeV2";
import SkillGapDetailV2 from "Pages/TalentMobilityV2/SkillGapDetailV2";
import CandidateProfileV2 from "Pages/TalentMobilityV2/CandidateProfileV2";
import LegalCompliancePage from "Pages/Legal/LegalCompliancePage";

// ─────────────────────────────────────────────────────────────────────────────

// A route is WIP-only when "inprogress" is the *sole* required entitlement
// (e.g. Dashboard, Skills Bridger's WIP sub-routes) — see "inprogress" in the
// docstring above. Shared by every "pick the first accessible route" call
// site (EntitlementGuard.jsx, ProtectedRoutesV2.jsx) so a WIP module can't
// become someone's landing page just by sitting earlier in this array; they
// demote it and fall back to it only if nothing else matches.
export const isWipOnly = (requiredEntitlements = []) =>
  requiredEntitlements.length === 1 && requiredEntitlements[0] === "inprogress";

export const routeConfigV2 = [
  // ── Jobs Library — V2 (Ant Design) ───────────────────────────────────────
  // online_user gets the read-only online view; all other roles get V2.
  {
    path: "/jobs-library",
    element: <JobsLibraryV2 />,
    resource: "jobs_library",
    requiredEntitlements: ["view"],
    resolveElement: (role) =>
      role === "online_user" ? <JobsLibraryOnline /> : <JobsLibraryV2 />,
  },

  // ── Job Details — V2 (Ant Design) ────────────────────────────────────────
  {
    path: "/jobs-library/job-details/:jobId/:sbUniqueId",
    element: <SbJobDetailsV2 />,
    resource: "jobs_library-job_details",
    requiredEntitlements: ["view"],
    resolveElement: (role) =>
      role === "online_user" ? <SbJobDetailsOnline /> : <SbJobDetailsV2 />,
  },
  // ── Job Details — no params (compare-only mode from library) ─────────────
  {
    path: "/jobs-library/job-details",
    element: <SbJobDetailsV2 />,
    resource: "jobs_library-job_details",
    requiredEntitlements: ["view"],
    resolveElement: (role) =>
      role === "online_user" ? <SbJobDetailsOnline /> : <SbJobDetailsV2 />,
  },

  // ── Job Compare ───────────────────────────────────────────────────────────
  {
    path: "/jobs-library/job-compare",
    element: <SbJobCompareV2 />,
    resource: "jobs_library",
    requiredEntitlements: ["compare_data"],
  },

  // ── Dashboard ─────────────────────────────────────────────────────────────
  // Gated on "inprogress" (not "view") — the backend currently only ever
  // grants "inprogress" on the "dashboard" resource, matching the WIP-feature
  // convention used by skillsbridger/roles below (see the docstring at the
  // top of this file). Note the V1 stack (SbLayoutComponent.jsx / RouteConfig.js)
  // uses "dashboard" with a different, older convention — "view" for full
  // access and a separate "inprogress" check just to show a disabled
  // "coming soon" nav link — so don't use it as a reference for this route.
  //
  // Placed after Jobs Library rather than first in this array as
  // defense-in-depth: SmartRedirect, the role-switch effect, and
  // EntitlementGuard's fallback (all in ProtectedRoutesV2.jsx / EntitlementGuard.jsx)
  // already demote WIP-only routes via isWipOnly() above when picking the
  // first accessible route, but keeping Dashboard out of the very first slot
  // means a role holding "inprogress" still doesn't hijack the landing page
  // even if that demotion is ever removed or missed at a future call site.
  {
    path: "/dashboard",
    element: <Dashboard />,
    resource: "dashboard",
    requiredEntitlements: ["inprogress"],
  },

  // ── People + AI ───────────────────────────────────────────────────────────
  {
    path: "/people+ai",
    element: <PeoplePlusAIV2 />,
    resource: "people_plus_ai",
    requiredEntitlements: ["view"],
  },
  {
    path: "/people+ai/agent-library",
    element: <SbAgentLibraryV2 />,
    resource: "people_plus_ai",
    requiredEntitlements: ["view_agent_library"],
  },
  {
    path: "/people+ai/pac-group-agents",
    element: <SbPACGroupAgentsV2 />,
    resource: "people_plus_ai",
    requiredEntitlements: ["view"],
  },
  {
    path: "/people+ai/pac-group-agents/agent-details",
    element: <SbPACGroupAgentDetailsV2 />,
    resource: "people_plus_ai",
    requiredEntitlements: ["view"],
  },
  {
    path: "/people+ai/pac-group-agents/role-impact",
    element: <SbPACRoleImpactV2 />,
    resource: "people_plus_ai",
    requiredEntitlements: ["view"],
  },
  {
    path: "/people+ai/pac-group-agents/role-impact/details",
    element: <SbPACRoleImpactDetailsV2 />,
    resource: "people_plus_ai",
    requiredEntitlements: ["view"],
  },
  {
    path: "/pac-jd-prd-view",
    element: <SbPACJdPrdViewV2 />,
    resource: "people_plus_ai",
    requiredEntitlements: ["view"],
  },
  {
    path: "/pac-people-jd-view",
    element: <SbPACPeopleJDViewV2 />,
    resource: "people_plus_ai",
    requiredEntitlements: ["view"],
  },

  // ── Skills Bridger (HR) ───────────────────────────────────────────────────
  {
    path: "/skills-bridger",
    element: <SkillsBridgerV2 />,
    resource: "skillsbridger",
    requiredEntitlements: ["inprogress"],
  },
  {
    path: "/Skills-bridger/group-skills-proficiency",
    element: <GroupSkillsProficiencyV2 />,
    resource: "skillsbridger",
    requiredEntitlements: ["inprogress"],
  },
  {
    path: "/Skills-bridger/employee-skills-proficiency",
    element: <GroupSkillsProficiencyV2 viewType="employee" />,
    resource: "skillsbridger",
    requiredEntitlements: ["inprogress"],
  },
  {
    path: "/skills-bridger/skills-map",
    element: <SbSkillsMap />,
    resource: "skillsbridger",
    requiredEntitlements: ["inprogress"],
  },
  {
    path: "/skills-bridger/group-skills-map",
    element: <GroupSkillsMapV2 />,
    resource: "skillsbridger",
    requiredEntitlements: ["view"],
  },
  {
    path: "/skills-bridger/employee-skills-map",
    element: <EmployeeSkillsMapV2 />,
    resource: "skillsbridger",
    requiredEntitlements: ["view"],
  },

  // ── Skills Bridger (Employee) ─────────────────────────────────────────────
  {
    path: "/Skills-bridger-employee",
    element: <EmployeeSkillsBridgerV2 />,
    resource: "skillsbridger_employee",
    requiredEntitlements: ["view"],
  },

  // ── Org Design (Ant Design) ───────────────────────────────────────────────
  // Gated by its own "org_redesign" entitlement resource (not people_plus_ai
  // — PAIC is only a data source it consumes, not its access gate).
  {
    path: "/org-design",
    element: <OrgDesignLanding />,
    resource: "org_redesign",
    requiredEntitlements: ["view"],
  },
  {
    path: "/org-design/workspace",
    element: <OrgDesignWorkspace />,
    resource: "org_redesign",
    requiredEntitlements: ["view"],
  },
  {
    path: "/org-design/paic-import",
    element: <PaicRecommendedImport />,
    resource: "org_redesign",
    requiredEntitlements: ["view"],
  },
  // ── Talent Mobility — V2 (Ant Design) ────────────────────────────────────
  // Manager view (HR Manager + Line Manager share one component tree — scope
  // differs, not authority; see Pages/TalentMobilityV2/useTalentMobilityData.js).
  // Backend now registers talent_mobility / talent_mobility_employee as real
  // entitlement features, so both are gated for real below.
  {
    path: "/talent-mobility",
    element: <TalentMobilityManagerV2 />,
    resource: "talent_mobility",
    requiredEntitlements: ["view"],
  },
  {
    path: "/talent-mobility/role/:roleId",
    element: <RoleDetailV2 />,
    resource: "talent_mobility",
    requiredEntitlements: ["view"],
  },
  {
    path: "/talent-mobility/succession-planning",
    element: <SuccessionPlanningV2 />,
    resource: "talent_mobility",
    requiredEntitlements: ["view"],
  },
  {
    path: "/talent-mobility/redeployment-center",
    element: <RedeploymentCenterV2 />,
    resource: "talent_mobility",
    requiredEntitlements: ["view"],
  },
  {
    path: "/talent-mobility/redeployment-center/reduction/:roleId",
    element: <RedeploymentReductionDetailV2 />,
    resource: "talent_mobility",
    requiredEntitlements: ["view"],
  },
  {
    path: "/talent-mobility/workforce-planning",
    element: <WorkforcePlanningV2 />,
    resource: "talent_mobility",
    requiredEntitlements: ["view"],
  },
  {
    path: "/talent-mobility/paic-org-redesign",
    element: <PaicOrgRedesignV2 />,
    resource: "talent_mobility",
    requiredEntitlements: ["view"],
  },
  {
    // Same component as the bare path above — PaicOrgRedesignV2 reads
    // :versionId (useParams) to re-open that specific version directly, so
    // the opened version survives a reload/shared link/"Back" navigation
    // instead of only ever living in local React state (which the bare path
    // alone can't restore).
    path: "/talent-mobility/paic-org-redesign/:versionId",
    element: <PaicOrgRedesignV2 />,
    resource: "talent_mobility",
    requiredEntitlements: ["view"],
  },
  {
    // Same Reduction Detail page Redeployment Center's own reduction route
    // uses, wrapped by PaicReductionDetailV2 so PAIC Org Redesign's "Roles
    // Reduced" table gets its own entry point: picks land in the PAIC plan
    // draft, the Save buttons save a PAIC plan, and "Back" returns to the
    // version the role was opened from (?versionId=).
    path: "/talent-mobility/paic-org-redesign/reduction/:roleId",
    element: <PaicReductionDetailV2 />,
    resource: "talent_mobility",
    requiredEntitlements: ["view"],
  },
  {
    path: "/talent-mobility/view-planning",
    element: <ViewPlanningV2 />,
    resource: "talent_mobility",
    requiredEntitlements: ["view"],
  },
  {
    path: "/talent-mobility/candidate/:personId/role/:roleId",
    element: <CandidateProfileV2 />,
    resource: "talent_mobility",
    requiredEntitlements: ["view"],
  },
  // Employee view
  {
    path: "/talent-mobility-employee",
    element: <TalentMobilityEmployeeV2 />,
    resource: "talent_mobility_employee",
    requiredEntitlements: ["view"],
  },
  {
    path: "/talent-mobility-employee/opportunity/:opportunityId",
    element: <SkillGapDetailV2 />,
    resource: "talent_mobility_employee",
    requiredEntitlements: ["view"],
  },

  // ── Learning Catalyst ─────────────────────────────────────────────────────
  {
    path: "/learning-catalyst",
    element: <LearningCatalyst />,
    resource: "learning_catalyst",
    requiredEntitlements: ["view"],
  },
  {
    path: "/learning-catalyst-employee",
    element: <LearningCatalyst />,
    resource: "learning_catalyst_employee",
    requiredEntitlements: ["view"],
  },

  // ── Admin Setup & Entitlements Management ────────────────────────────────
  {
    path: "/admin-setup",
    element: <AdminSetup />,
    resource: "admin_setup",
    requiredEntitlements: ["view"],
  },
  {
    path: "/profile",
    element: <ProfileManagementV2 />,
    resource: "profile",
    requiredEntitlements: ["view"],
  },
  // Read-only admin view of all job codes, accessed from Manage Profiles.
  // Separate route justified: different access context (profile resource), own URL, and independent data lifecycle from Jobs Library.
  {
    path: "/profile/all-jobs",
    element: <SbAdminAllJobsView />,
    resource: "profile",
    requiredEntitlements: ["view"],
  },
  {
    path: "/profile/upload-jd",
    element: <SbUploadJd />,
    resource: "profile",
    requiredEntitlements: ["create"],
  },
  {
    path: "/employees",
    element: <EmployeeManagementV2 />,
    resource: "employee",
    requiredEntitlements: ["view"],
  },
  {
    path: "/admin-setup/org-tech-context-setup",
    element: <OrgTechContextSetup />,
    resource: "org_tech_context_setup",
    requiredEntitlements: ["view", "edit"],
  },
  {
    path: "/admin-setup/employee-function-access",
    element: <EmployeeFunctionAccess />,
    resource: "user_access_function",
    requiredEntitlements: ["view"],
  },
  {
    path: "/admin-setup/compensation",
    element: <CompensationManagement />,
    resource: "admin_setup",
    requiredEntitlements: ["view"],
  },
  {
    path: "/admin-setup/talent-mobility-settings",
    element: <TalentMobilitySettings />,
    resource: "talent_mobility_admin",
    requiredEntitlements: ["view"],
  },
  {
    path: "/admin-setup/employee-data",
    element: <OrganizationHierarchy />,
    resource: "admin_setup",
    requiredEntitlements: ["view"],
  },
  {
    path: "/admin-setup/job-codes",
    element: <AdminJobCodesPage />,
    resource: "admin_setup",
    requiredEntitlements: ["view"],
  },
  {
    path: "/admin-setup/role-reporting",
    element: <RoleReporting />,
    resource: "admin_setup",
    requiredEntitlements: ["view"],
  },
  {
    path: "/admin-setup/jd-archive",
    element: <JdArchive />,
    resource: "admin_setup",
    requiredEntitlements: ["view"],
  },
  {
    // Ownership Transfer (Ownership design, part 4): HR Admins, the same right its card and endpoints check
    path: "/admin-setup/ownership-transfer",
    element: <OwnershipTransfer />,
    resource: "admin_setup",
    requiredEntitlements: ["edit"],
  },
  {
    path: "/admin-setup/upload-external-jd",
    element: <AdminSetupUploadJd />,
    resource: "admin_setup-upload_external_jd",
    requiredEntitlements: ["create"],
  },
  {
    path:"/admin-setup/jd-templates",
    element: <JdTemplates />,
    resource: "admin_setup",
    requiredEntitlements: ["view"],
  },
  {
    path:"/admin-setup/jd-templates/:id",
    element: <JdTemplateDetail />,
    resource: "admin_setup",
    requiredEntitlements: ["view"],
  },
  {
    path:"/admin-setup/jd-management",
    element: <JdManagement />,
    resource: "admin_setup",
    requiredEntitlements: ["view"],
  },

  // ── System Admin Setup ────────────────────────────────────────────────────
  {
    path: "/system-admin-setup",
    element: <SystemAdminSetup />,
    resource: "system_admin_setup",
    requiredEntitlements: ["view"],
  },
  {
    path: "/system-admin-setup/org-registry-management",
    element: <OrgRegistryManagement />,
    resource: "system_admin_setup-org_registry_management",
    requiredEntitlements: ["view"],
  },
  {
    path:"/admin-setup/legal-entity",
    element: <LegalEntityManagement />,
    resource: "admin_setup",
    requiredEntitlements: ["view"],
  },

  // ── Legal & Consent ───────────────────────────────────────────────────────
  // Enabled per org by the BE; the page redirects home when disabled.
  { path: "/legal", element: <LegalCompliancePage /> },
];
